import { useEffect, useId, useRef, useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import BrandMark from './BrandMark.jsx';
import HeaderSearch from './HeaderSearch.jsx';
import HeaderCreateMenu from './HeaderCreateMenu.jsx';
import HeaderMessagesButton from './HeaderMessagesButton.jsx';
import { ACCOUNT_NAV, PRIMARY_TABS, getActiveNav } from './ClientSidebar.jsx';

export default function ClientMobileHeader({
  matters = [],
  evidenceMatters = null,
  debtors = [],
  navTabs = null,
  settingsPath = '',
  canWrite = true,
  onOpenMatter,
  onNewDemand,
  onNewDebtor,
  onSignOut,
  user,
  messagesViewer = {},
}) {
  const location = useLocation();
  const { active } = getActiveNav(location.pathname, location.search);
  const [open, setOpen] = useState(false);
  const menuId = useId();
  const rootRef = useRef(null);
  const menuItems = [
    ...(navTabs || PRIMARY_TABS),
    ...ACCOUNT_NAV.map((item) =>
      item.id === 'settings' && settingsPath ? { ...item, to: settingsPath } : item
    ),
  ];

  useEffect(() => {
    setOpen(false);
  }, [location.pathname, location.search]);

  useEffect(() => {
    if (!open) return undefined;

    function onPointerDown(event) {
      if (!rootRef.current?.contains(event.target)) {
        setOpen(false);
      }
    }

    function onKeyDown(event) {
      if (event.key === 'Escape') setOpen(false);
    }

    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  return (
    <header className="client-mobile-header" ref={rootRef}>
      <NavLink to="/client/home" className="client-mobile-header-brand" aria-label="DemandJet home">
        <BrandMark
          variant="markOnDark"
          size={44}
          className="client-mobile-header-logo"
          alt=""
        />
      </NavLink>

      <div className="client-mobile-header-actions">
        {canWrite ? (
          <HeaderCreateMenu onNewDemand={onNewDemand} onNewDebtor={onNewDebtor} />
        ) : null}
        <HeaderMessagesButton matters={matters} viewer={messagesViewer} />
        <HeaderSearch
          matters={matters}
          evidenceMatters={evidenceMatters}
          debtors={debtors}
          onOpenMatter={onOpenMatter}
        />

        <div className="client-mobile-menu-wrap">
          <button
            type="button"
            className="client-mobile-menu-toggle"
            aria-expanded={open}
            aria-controls={menuId}
            aria-label={open ? 'Close menu' : 'Open menu'}
            onClick={() => setOpen((value) => !value)}
          >
            {open ? <CloseIcon /> : <MenuIcon />}
          </button>

          {open ? (
            <div className="client-mobile-menu" id={menuId} role="menu" aria-label="Application menu">
              <p className="client-mobile-menu-label">
                {active.id === 'messages' ? 'Messages' : active.label}
              </p>
              {user?.email ? (
                <p className="client-mobile-menu-user">{user.email}</p>
              ) : null}
              <ul className="client-mobile-menu-list">
                {menuItems.map((item) => {
                  const isActive = active.id === item.id;
                  return (
                    <li key={item.id} role="none">
                      <NavLink
                        to={item.to}
                        role="menuitem"
                        className={
                          isActive
                            ? 'client-mobile-menu-item is-active'
                            : 'client-mobile-menu-item'
                        }
                        onClick={() => setOpen(false)}
                      >
                        {item.label}
                      </NavLink>
                    </li>
                  );
                })}
              </ul>
              <div className="client-mobile-menu-footer">
                <button
                  type="button"
                  className="client-mobile-menu-item"
                  role="menuitem"
                  onClick={() => {
                    setOpen(false);
                    onSignOut?.();
                  }}
                >
                  Sign out
                </button>
              </div>
            </div>
          ) : null}
        </div>
      </div>
    </header>
  );
}

function MenuIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path
        d="M4 7h16M4 12h16M4 17h16"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
      />
    </svg>
  );
}

function CloseIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path
        d="M6 6l12 12M18 6 6 18"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
      />
    </svg>
  );
}
